import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Send, AlertCircle, Key, ChevronDown } from "lucide-react";
import { questions } from "@/data/questions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Questionnaire = () => {
  const navigate = useNavigate();
  // Steps: -2 = code input, -1 = identification (posto), 0+ = questions
  const [currentStep, setCurrentStep] = useState(-2);
  const [accessCode, setAccessCode] = useState("");
  const [codeValidated, setCodeValidated] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [formData, setFormData] = useState({
    empresa: "",
    setor: "",
    funcao: "",
  });
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Hide Emergent badge on mobile for questionnaire
  useEffect(() => {
    const hideEmergentBadge = () => {
      // Find and hide the Emergent badge
      const badges = document.querySelectorAll('div[style*="position: fixed"]');
      badges.forEach(badge => {
        if (badge.textContent?.includes('Emergent') || badge.innerHTML?.includes('Emergent')) {
          badge.style.display = 'none';
        }
      });
      // Also check for iframes that might contain the badge
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        if (iframe.style.position === 'fixed') {
          iframe.style.display = 'none';
        }
      });
    };

    // Run immediately and with a delay to catch dynamically loaded elements
    hideEmergentBadge();
    const interval = setInterval(hideEmergentBadge, 500);
    
    return () => {
      clearInterval(interval);
      // Restore badges when leaving questionnaire
      const badges = document.querySelectorAll('div[style*="position: fixed"]');
      badges.forEach(badge => {
        badge.style.display = '';
      });
    };
  }, []);

  const isCodeStep = currentStep === -2;
  const isIdentificationStep = currentStep === -1;
  const isQuestionStep = currentStep >= 0;
  const totalSteps = questions.length + 2;
  const progress = isCodeStep ? 0 : isIdentificationStep ? 5 : ((currentStep + 1) / questions.length) * 100;
  const isLastQuestion = currentStep === questions.length - 1;

  const getAvailableFunctions = () => {
    if (!companyData || !formData.setor) return [];
    const sector = companyData.sectors_data?.find(s => s.name === formData.setor);
    return sector?.functions || [];
  };

  const validateIdentification = () => {
    const newErrors = {};
    if (!formData.setor.trim()) newErrors.setor = "Selecione o setor";
    if (!formData.funcao.trim()) newErrors.funcao = "Informe a função";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAccessCode = async () => {
    if (!accessCode.trim()) {
      setErrors({ code: "Digite o código de acesso" });
      return false;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/validate-code`, {
        access_code: accessCode.toUpperCase()
      });

      setCompanyData(response.data);
      setFormData({
        empresa: response.data.empresa,
        setor: "",
        funcao: ""
      });
      setCodeValidated(true);
      setErrors({});
      return true;
    } catch (error) {
      if (error.response?.status === 403) {
        setErrors({ code: "Acesso desabilitado para esta empresa. Contate o administrador." });
      } else {
        setErrors({ code: "Código de acesso inválido" });
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    const valid = await validateAccessCode();
    if (valid) {
      setCurrentStep(-1); // Go to identification step
    }
  };

  const handleStartQuestionnaire = () => {
    if (validateIdentification()) {
      setCurrentStep(0); // Start questions
    }
  };

  const handleNext = () => {
    if (!answers[currentStep]) {
      setErrors({ question: "Por favor, selecione uma resposta" });
      return;
    }
    setErrors({});
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setErrors({});
    if (isCodeStep) {
      navigate("/");
    } else if (isIdentificationStep) {
      setCurrentStep(-2);
      setCodeValidated(false);
      setCompanyData(null);
    } else if (currentStep === 0) {
      setCurrentStep(-1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!answers[currentStep]) {
      setErrors({ question: "Por favor, selecione uma resposta" });
      return;
    }

    setLoading(true);
    try {
      const responses = questions.map((q, idx) => ({
        dimension: q.dimension,
        question: q.text,
        answer: answers[idx],
      }));

      await axios.post(`${API}/assessments`, {
        ...formData,
        access_code: accessCode,
        responses,
      });

      navigate("/success");
    } catch (error) {
      console.error("Erro ao enviar avaliação:", error);
      setErrors({ submit: "Erro ao enviar. Tente novamente." });
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = isQuestionStep ? questions[currentStep] : null;

  // Code Input Screen
  if (isCodeStep) {
    return (
      <div className="min-h-screen md:min-h-screen h-[100dvh] md:h-auto bg-gradient-to-br from-orange-50 via-white to-orange-50/30 py-4 md:py-8 px-4 flex flex-col questionnaire-page">
        <div className="max-w-lg mx-auto flex-1 flex flex-col justify-center">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl shadow-orange-100/50 p-6 md:p-12">
            <div className="text-center">
              <div className="w-14 h-14 md:w-20 md:h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <Key className="w-7 h-7 md:w-10 md:h-10 text-orange-600" />
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-4">
                Código de Acesso
              </h2>
              
              <p className="text-gray-600 text-sm md:text-base mb-4 md:mb-8">
                Digite o código de acesso fornecido pela sua empresa para iniciar a avaliação.
              </p>

              <div className="max-w-sm mx-auto">
                <input
                  data-testid="access-code-input"
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
                  className="w-full h-12 md:h-14 px-4 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all outline-none text-center text-xl md:text-2xl font-mono font-bold tracking-wider"
                  placeholder="ABC123"
                  maxLength={10}
                />
                {errors.code && (
                  <p className="text-red-500 text-sm mt-2 flex items-center justify-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.code}
                  </p>
                )}
              </div>

              <div className="mt-4 md:mt-8 bg-blue-50 border border-blue-200 rounded-xl md:rounded-2xl p-3 md:p-4 max-w-md mx-auto">
                <p className="text-xs md:text-sm text-gray-700">
                  <strong>Não possui um código?</strong><br />
                  Entre em contato com o RH ou o responsável pela segurança do trabalho.
                </p>
              </div>

              <div className="flex justify-between mt-4 md:mt-8 pt-4 md:pt-8 border-t border-gray-100">
                <button
                  data-testid="back-button"
                  onClick={() => navigate("/")}
                  className="flex items-center gap-1 md:gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-full text-gray-600 hover:bg-gray-100 transition-all font-medium text-sm md:text-base"
                >
                  <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
                  Voltar
                </button>

                <button
                  data-testid="next-button"
                  onClick={handleCodeSubmit}
                  disabled={loading}
                  className="flex items-center gap-1 md:gap-2 px-6 md:px-8 py-2.5 md:py-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-semibold shadow-lg shadow-orange-500/30 disabled:opacity-50 text-sm md:text-base"
                >
                  {loading ? "Validando..." : "Continuar"}
                  <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Identification Screen (Posto)
  if (isIdentificationStep) {
    return (
      <div className="min-h-screen md:min-h-screen h-[100dvh] md:h-auto bg-gradient-to-br from-orange-50 via-white to-orange-50/30 py-4 md:py-8 px-3 md:px-4 flex flex-col questionnaire-page">
        <div className="max-w-2xl mx-auto flex-1 flex flex-col">
          {/* Header */}
          <div className="text-center mb-4 md:mb-8">
            <h1 className="text-xl md:text-4xl font-bold text-gray-900 mb-1 md:mb-3">
              Avaliação de Riscos Psicossociais
            </h1>
            <p className="text-gray-600 text-sm md:text-lg">
              Questionário COPSOQ II (Versão Curta) em conformidade com a nova NR-01.
            </p>
            
            <div className="flex items-center justify-center gap-2 mt-3 md:mt-6">
              <span className="text-xs md:text-sm text-gray-500 uppercase tracking-wide font-semibold">Acesso Autorizado:</span>
              <span className="px-2 md:px-3 py-0.5 md:py-1 bg-green-100 text-green-700 font-mono font-bold text-xs md:text-sm rounded border border-green-200">
                {accessCode}
              </span>
            </div>
          </div>

          {/* Identification Card */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-xl shadow-orange-100/50 p-4 md:p-8 flex-1">
            <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
              <div className="w-1 h-6 md:h-8 bg-orange-500 rounded-full"></div>
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-900">Identificação do Posto</h2>
                <p className="text-gray-600 text-xs md:text-base mt-0.5 md:mt-1">Selecione seu setor e função para iniciar a avaliação técnica.</p>
              </div>
            </div>

            <div className="space-y-3 md:space-y-6">
              {/* Empresa - readonly */}
              <div>
                <label className="block text-[10px] md:text-xs font-bold text-gray-500 mb-1 md:mb-2 uppercase tracking-wide">
                  Empresa
                </label>
                <div className="w-full h-10 md:h-12 px-3 md:px-4 rounded-lg md:rounded-xl bg-gray-100 border border-gray-200 flex items-center text-gray-800 font-medium text-sm md:text-base">
                  {formData.empresa}
                </div>
              </div>

              {/* Setor and Funcao in grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label className="block text-[10px] md:text-xs font-bold text-gray-500 mb-1 md:mb-2 uppercase tracking-wide">
                    Setor / Departamento
                  </label>
                  {companyData?.sectors_data && companyData.sectors_data.length > 0 ? (
                    <div className="relative">
                      <select
                        data-testid="setor-select"
                        value={formData.setor}
                        onChange={(e) => setFormData({ ...formData, setor: e.target.value, funcao: "" })}
                        className="w-full h-10 md:h-12 px-3 md:px-4 pr-8 md:pr-10 rounded-lg md:rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all outline-none bg-white appearance-none text-gray-700 text-sm md:text-base"
                      >
                        <option value="">Selecione o setor...</option>
                        {companyData.sectors_data.map((sector, idx) => (
                          <option key={idx} value={sector.name}>{sector.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-gray-400 absolute right-2 md:right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  ) : (
                    <input
                      data-testid="setor-input"
                      type="text"
                      value={formData.setor}
                      onChange={(e) => setFormData({ ...formData, setor: e.target.value })}
                      className="w-full h-10 md:h-12 px-3 md:px-4 rounded-lg md:rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all outline-none text-sm md:text-base"
                      placeholder="Digite o setor"
                    />
                  )}
                  {errors.setor && (
                    <p className="text-red-500 text-[10px] md:text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.setor}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] md:text-xs font-bold text-gray-500 mb-1 md:mb-2 uppercase tracking-wide">
                    Função Avaliada
                  </label>
                  {getAvailableFunctions().length > 0 ? (
                    <div className="relative">
                      <select
                        data-testid="funcao-select"
                        value={formData.funcao}
                        onChange={(e) => setFormData({ ...formData, funcao: e.target.value })}
                        className="w-full h-10 md:h-12 px-3 md:px-4 pr-8 md:pr-10 rounded-lg md:rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all outline-none bg-white appearance-none text-gray-700 text-sm md:text-base"
                      >
                        <option value="">Selecione a função...</option>
                        {getAvailableFunctions().map((func, idx) => (
                          <option key={idx} value={func}>{func}</option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-gray-400 absolute right-2 md:right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  ) : (
                    <input
                      data-testid="funcao-input"
                      type="text"
                      value={formData.funcao}
                      onChange={(e) => setFormData({ ...formData, funcao: e.target.value })}
                      className="w-full h-10 md:h-12 px-3 md:px-4 rounded-lg md:rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-all outline-none text-sm md:text-base"
                      placeholder="Ex: Operador I"
                    />
                  )}
                  {errors.funcao && (
                    <p className="text-red-500 text-[10px] md:text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.funcao}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                data-testid="start-questionnaire-btn"
                onClick={handleStartQuestionnaire}
                className="w-full h-12 md:h-14 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-lg md:rounded-xl hover:from-gray-800 hover:to-gray-900 transition-all font-bold text-sm md:text-lg uppercase tracking-wide shadow-lg"
              >
                Responder Questionário
              </button>

              {/* LGPD Notice */}
              <p className="text-center text-[10px] md:text-xs text-gray-500">
                Em conformidade com a LGPD e a NR-01. Respostas tratadas de forma estatística.
              </p>
            </div>
          </div>

          {/* Back link */}
          <div className="text-center mt-3 md:mt-6">
            <button
              onClick={handleBack}
              className="text-gray-500 hover:text-gray-700 text-xs md:text-sm flex items-center gap-1 mx-auto"
            >
              <ChevronLeft className="w-3 h-3 md:w-4 md:h-4" />
              Voltar e usar outro código
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Questions Screen
  return (
    <div className="min-h-screen md:min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 py-3 md:py-8 px-3 md:px-4 questionnaire-page">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-3 md:mb-8">
          <div className="flex justify-between items-center mb-1 md:mb-2">
            <span className="text-xs md:text-sm font-medium text-gray-600">
              Questão {currentStep + 1} de {questions.length}
            </span>
            <span className="text-xs md:text-sm font-medium text-orange-600">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full h-1.5 md:h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl shadow-orange-100/50 p-4 md:p-12">
          {currentQuestion && (
            <div data-testid={`question-${currentStep}`}>
              <div className="inline-block bg-orange-100 text-orange-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-semibold mb-2 md:mb-4">
                {currentQuestion.dimension}
              </div>
              <h3 className="text-base md:text-2xl font-bold text-gray-900 mb-4 md:mb-8 leading-relaxed">
                {currentQuestion.text}
              </h3>

              <div className="space-y-2 md:space-y-3">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    data-testid={`option-${idx}`}
                    onClick={() => {
                      setAnswers({ ...answers, [currentStep]: idx + 1 });
                      setErrors({});
                    }}
                    className={`w-full p-3 md:p-4 rounded-lg md:rounded-xl border-2 text-left transition-all ${
                      answers[currentStep] === idx + 1
                        ? "border-orange-500 bg-orange-50 shadow-md"
                        : "border-gray-200 hover:border-orange-200 hover:bg-orange-50/50"
                    }`}
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div
                        className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          answers[currentStep] === idx + 1
                            ? "border-orange-500 bg-orange-500"
                            : "border-gray-300"
                        }`}
                      >
                        {answers[currentStep] === idx + 1 && (
                          <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <span className="font-medium text-gray-800 text-sm md:text-base">{option}</span>
                    </div>
                  </button>
                ))}
              </div>

              {errors.question && (
                <p className="text-red-500 text-xs md:text-sm mt-2 md:mt-4 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 md:w-4 md:h-4" />
                  {errors.question}
                </p>
              )}

              {/* Button moved right after options */}
              <div className="flex justify-center mt-4 md:mt-8">
                {!isLastQuestion ? (
                  <button
                    data-testid="next-button"
                    onClick={handleNext}
                    className="flex items-center gap-1 md:gap-2 px-6 md:px-8 py-2.5 md:py-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-semibold shadow-lg shadow-orange-500/30 text-sm md:text-base"
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                ) : (
                  <button
                    data-testid="submit-button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex items-center gap-1 md:gap-2 px-6 md:px-8 py-2.5 md:py-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-semibold shadow-lg shadow-orange-500/30 disabled:opacity-50 text-sm md:text-base"
                  >
                    {loading ? "Enviando..." : "Finalizar"}
                    <Send className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {errors.submit && (
            <div className="mt-4 md:mt-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg md:rounded-xl text-red-700 flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 md:w-5 md:h-5" />
              {errors.submit}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
